import React, { useState } from 'react';
import { LinkIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';

/**
 * TopEndpointsTable - Table showing top endpoints with metrics
 */
const TopEndpointsTable = ({ data, loading }) => {
  const [sortField, setSortField] = useState('count');
  const [sortOrder, setSortOrder] = useState('desc');

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-40 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Extract endpoints array from API response (API returns { endpoints: [...], total: N })
  const endpoints = data?.endpoints || data || [];

  if (!endpoints || endpoints.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          Top Endpoints
        </h3>
        <div className="h-48 flex items-center justify-center text-gray-500">
          No endpoint data available
        </div>
      </div>
    );
  }

  // Sort data
  const sortedData = [...endpoints].sort((a, b) => {
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const totalRequests = endpoints.reduce((sum, item) => sum + (item.count || 0), 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
        <LinkIcon className="w-4 h-4" />
        Top Endpoints
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-2 font-medium text-gray-500">Endpoint</th>
              <th className="text-left py-2 px-2 font-medium text-gray-500">Method</th>
              <SortableHeader
                label="Requests"
                field="count"
                currentField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <SortableHeader
                label="Avg (ms)"
                field="avgResponseTime"
                currentField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <SortableHeader
                label="Errors"
                field="errorCount"
                currentField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <th className="text-right py-2 px-2 font-medium text-gray-500">%</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.slice(0, 15).map((endpoint, index) => (
              <tr
                key={index}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-2 px-2">
                  <div className="max-w-[200px] truncate text-gray-700" title={endpoint.path}>
                    {endpoint.path}
                  </div>
                </td>
                <td className="py-2 px-2">
                  <MethodBadge method={endpoint.method} />
                </td>
                <td className="py-2 px-2 text-right text-gray-700">
                  {(endpoint.count || 0).toLocaleString()}
                </td>
                <td className="py-2 px-2 text-right">
                  <ResponseTimeBadge time={endpoint.avgResponseTime} />
                </td>
                <td className="py-2 px-2 text-right">
                  <span className={endpoint.errorCount > 0 ? 'text-red-600' : 'text-gray-400'}>
                    {endpoint.errorCount || 0}
                  </span>
                </td>
                <td className="py-2 px-2 text-right text-gray-500">
                  {((endpoint.count / totalRequests) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {endpoints.length > 15 && (
        <div className="mt-2 text-xs text-gray-500 text-center">
          Showing top 15 of {endpoints.length} endpoints
        </div>
      )}
    </div>
  );
};

/**
 * Sortable header component
 */
const SortableHeader = ({ label, field, currentField, sortOrder, onSort }) => (
  <th
    className="text-right py-2 px-2 font-medium text-gray-500 cursor-pointer hover:text-gray-700"
    onClick={() => onSort(field)}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      <ArrowsUpDownIcon
        className={`w-3 h-3 ${currentField === field ? 'text-indigo-600' : ''}`}
      />
    </span>
  </th>
);

/**
 * HTTP method badge
 */
const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-green-100 text-green-700',
    POST: 'bg-blue-100 text-blue-700',
    PUT: 'bg-yellow-100 text-yellow-700',
    PATCH: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
    HEAD: 'bg-gray-100 text-gray-700',
    OPTIONS: 'bg-purple-100 text-purple-700'
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[method] || 'bg-gray-100 text-gray-700'}`}>
      {method || 'GET'}
    </span>
  );
};

/**
 * Response time badge with color coding
 */
const ResponseTimeBadge = ({ time }) => {
  const ms = Math.round((time || 0) * 1000);

  let colorClass = 'text-green-600';
  if (ms > 1000) colorClass = 'text-red-600';
  else if (ms > 500) colorClass = 'text-yellow-600';

  return <span className={colorClass}>{ms}</span>;
};

export default TopEndpointsTable;
